export class ImageHelper {

    static addBase64MimeMarker(base64: string): string {
        if (!base64) return '';
        if (base64.startsWith('data:image')) {
            return base64;
        }

        if (base64.startsWith('/9j/')) {
            return `data:image/jpeg;base64,${base64}`;
        } else if (base64.startsWith('iVBORw0KGgo')) {
            return `data:image/png;base64,${base64}`;
        } else if (base64.startsWith('R0lGOD')) {
            return `data:image/gif;base64,${base64}`;
        } else if (base64.startsWith('UklGR')) {
            return `data:image/webp;base64,${base64}`;
        }
        return `data:application/octet-stream;base64,${base64}`;
    }


    /**
     * Redimensionne une image (sous forme de Blob) si ses dimensions dépassent maxWidth ou maxHeight.
     *
     * @param blob - Le blob de l'image à redimensionner.
     * @param maxWidth - La largeur maximale autorisée.
     * @param maxHeight - La hauteur maximale autorisée.
     * @returns Une promesse résolvant avec le blob redimensionné ou le blob d'origine.
     */
    public static resizeImageBlobIfNeeded(blob: Blob, maxWidth: number, maxHeight: number): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const tempUrl = URL.createObjectURL(blob);
            const img = new Image();

            img.onload = () => {
                const { width, height } = img;

                if (width > maxWidth || height > maxHeight) {
                    // Calcul du ratio pour garder le même ratio d'origine
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    const newWidth = Math.floor(width * ratio);
                    const newHeight = Math.floor(height * ratio);

                    // Création d'un canvas et redimensionnement de l'image
                    const canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    const ctx = canvas.getContext('2d');
                    if (!ctx) {
                        reject(new Error("Impossible de récupérer le contexte du canvas"));
                        return;
                    }
                    ctx.drawImage(img, 0, 0, newWidth, newHeight);

                    // Conversion du canvas en blob en respectant le type original de l'image
                    canvas.toBlob((resizedBlob) => {
                        if (resizedBlob) {
                            resolve(resizedBlob);
                        } else {
                            reject(new Error("Erreur lors de la conversion du canvas en blob"));
                        }
                    }, blob.type);
                } else {
                    // L'image respecte déjà les dimensions maximales, on retourne le blob d'origine
                    resolve(blob);
                }
                // Nettoyage de l'URL temporaire
                URL.revokeObjectURL(tempUrl);
            };

            img.onerror = (error) => {
                URL.revokeObjectURL(tempUrl);
                reject(error);
            };

            img.src = tempUrl;
        });
    }

    /**
     * Combine deux images à partir de leurs URLs.
     * @param url1 URL de la première image (fond)
     * @param url2 URL de la seconde image (superposition)
     * @returns Une promesse qui résout en un Blob de l'image combinée.
     */
    static combineImagesFromUrls(url1: string, url2: string): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const image1 = new Image();
            const image2 = new Image();

            // Pour éviter les problèmes CORS
            image1.crossOrigin = "anonymous";
            image2.crossOrigin = "anonymous";

            image1.src = url1;
            image2.src = url2;

            let loaded = 0;
            const onLoad = () => {
                loaded++;
                if (loaded === 2) {
                    // Définir la taille du canvas en fonction de la première image
                    const canvas = document.createElement("canvas");
                    canvas.width = image1.width;
                    canvas.height = image1.height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        return reject(new Error("Impossible de créer le contexte du canvas"));
                    }
                    // Dessiner l'image de fond
                    ctx.drawImage(image1, 0, 0, canvas.width, canvas.height);
                    // Dessiner l'image de superposition
                    ctx.drawImage(image2, 0, 0, canvas.width, canvas.height);
                    // Convertir le contenu du canvas en Blob
                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error("Échec de la conversion du canvas en Blob"));
                        }
                    }, "image/png");
                }
            };

            image1.onload = onLoad;
            image2.onload = onLoad;
            image1.onerror = () => reject(new Error("Erreur lors du chargement de l'image 1"));
            image2.onerror = () => reject(new Error("Erreur lors du chargement de l'image 2"));
        });
    }

    /**
     * Combine deux images à partir de leurs chaînes en base64.
     * Les chaînes doivent être au format dataURL (ex. "data:image/png;base64,...").
     * @param base64_1 Première image en base64 (fond)
     * @param base64_2 Seconde image en base64 (superposition)
     * @returns Une promesse qui résout en un Blob de l'image combinée.
     */
    static combineImagesFromBase64(
        base64_1: string,
        base64_2: string
    ): Promise<Blob> {
        // Les chaînes base64 étant des sources d'images valides, on réutilise la fonction par URL.
        return ImageHelper.combineImagesFromUrls(base64_1, base64_2);
    }

    /**
     * Combine deux images à partir de Blob.
     * @param blob1 Blob de la première image (fond)
     * @param blob2 Blob de la seconde image (superposition)
     * @returns Une promesse qui résout en un Blob de l'image combinée.
     */
    static combineImagesFromBlobs(blob1: Blob, blob2: Blob): Promise<Blob> {
        return new Promise((resolve, reject) => {
            // Créer des URLs temporaires à partir des blobs
            const src1 = URL.createObjectURL(blob1);
            const src2 = URL.createObjectURL(blob2);

            const image1 = new Image();
            const image2 = new Image();

            image1.crossOrigin = "anonymous";
            image2.crossOrigin = "anonymous";

            image1.src = src1;
            image2.src = src2;

            let loaded = 0;
            const onLoad = () => {
                loaded++;
                if (loaded === 2) {
                    const canvas = document.createElement("canvas");
                    canvas.width = image1.width;
                    canvas.height = image1.height;
                    const ctx = canvas.getContext("2d");
                    if (!ctx) {
                        URL.revokeObjectURL(src1);
                        URL.revokeObjectURL(src2);
                        return reject(new Error("Impossible de créer le contexte du canvas"));
                    }
                    ctx.drawImage(image1, 0, 0, canvas.width, canvas.height);
                    ctx.drawImage(image2, 0, 0, canvas.width, canvas.height);

                    // Libérer les URLs temporaires
                    URL.revokeObjectURL(src1);
                    URL.revokeObjectURL(src2);

                    canvas.toBlob((blob) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error("Échec de la conversion du canvas en Blob"));
                        }
                    }, "image/png");
                }
            };

            image1.onload = onLoad;
            image2.onload = onLoad;
            image1.onerror = () => {
                URL.revokeObjectURL(src1);
                URL.revokeObjectURL(src2);
                reject(new Error("Erreur lors du chargement de l'image 1"));
            };
            image2.onerror = () => {
                URL.revokeObjectURL(src1);
                URL.revokeObjectURL(src2);
                reject(new Error("Erreur lors du chargement de l'image 2"));
            };
        });
    }
}